import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  UserMinus,
  X,
  Package,
  FileX,
  MessageSquare,
  BookOpen,
  Shield,
  Calculator,
  FileText,
  Clock,
  Info,
  Loader2,
} from 'lucide-react';
import { Checkbox } from '@/components/ui/Checkbox';

interface Employee {
  id: string;
  name: string;
  employeeId: string;
  department: string;
}

interface EosStatus {
  isPaid: boolean;
}

interface OffboardingProcedure {
  id: string;
  title: string;
  description: string;
  estimatedDays: number;
  icon: string;
}

interface ContactDetails {
  personalEmail: string;
  personalPhone: string;
  emergencyContact: string;
}

interface OffboardingDataType {
  selectedProcedures: string[];
  priorityLevel: string;
  expectedCompletionDate: string;
  assignedTo: string;
  notes: string;
  contactDetails: ContactDetails;
}

interface OffboardingRecord extends OffboardingDataType {
  employeeId: string;
  employeeName: string;
  startedAt: string;
  startedBy: string;
  estimatedDuration: number;
  status: string;
  procedures: {
    id: string;
    title: string;
    description: string;
    estimatedDays: number;
    icon: string;
    status: string;
    assignedAt: string;
  }[];
}

interface Props {
  isOpen: boolean;
  onClose: () => void;
  employee: Employee;
  eosStatus: EosStatus;
  onOffboardingStarted: (record: OffboardingRecord) => void;
}

const iconMap: Record<string, React.ReactNode> = {
  Package: <Package className="w-4 h-4" />,
  FileX: <FileX className="w-4 h-4" />,
  MessageSquare: <MessageSquare className="w-4 h-4" />,
  BookOpen: <BookOpen className="w-4 h-4" />,
  Shield: <Shield className="w-4 h-4" />,
  Calculator: <Calculator className="w-4 h-4" />,
  FileText: <FileText className="w-4 h-4" />,
};

export default function OffboardingWorkflowModal({
  isOpen,
  onClose,
  employee,
  eosStatus,
  onOffboardingStarted,
}: Props) {
  const { t } = useTranslation();
  const [offboardingData, setOffboardingData] = useState<OffboardingDataType>({
    selectedProcedures: [],
    priorityLevel: 'standard',
    expectedCompletionDate: '',
    assignedTo: '',
    notes: '',
    contactDetails: {
      personalEmail: '',
      personalPhone: '',
      emergencyContact: '',
    },
  });

  const [isProcessing, setIsProcessing] = useState(false);

  const offboardingProcedures: OffboardingProcedure[] = [
    {
      id: 'asset_return',
      title: t('Asset Return'),
      description: t('Collect laptop, mobile phone, access cards, and other company assets'),
      estimatedDays: 2,
      icon: 'Package',
    },
    {
      id: 'visa_cancellation',
      title: t('Visa Cancellation'),
      description: t('Process visa cancellation and labour card termination'),
      estimatedDays: 7,
      icon: 'FileX',
    },
    {
      id: 'exit_interview',
      title: t('Exit Interview'),
      description: t('Conduct exit interview and collect feedback'),
      estimatedDays: 1,
      icon: 'MessageSquare',
    },
    {
      id: 'knowledge_transfer',
      title: t('Knowledge Transfer'),
      description: t('Document handover and knowledge transfer sessions'),
      estimatedDays: 3,
      icon: 'BookOpen',
    },
    {
      id: 'access_revocation',
      title: t('Access Revocation'),
      description: t('Revoke system access, email accounts, and security permissions'),
      estimatedDays: 1,
      icon: 'Shield',
    },
    {
      id: 'final_settlement',
      title: t('Final Settlement'),
      description: t('Complete final salary calculation and clearance documentation'),
      estimatedDays: 2,
      icon: 'Calculator',
    },
    {
      id: 'documentation',
      title: t('Documentation'),
      description: t('Prepare service certificate, experience letter, and clearance forms'),
      estimatedDays: 2,
      icon: 'FileText',
    },
  ];

  const handleProcedureToggle = (procedureId: string) => {
    setOffboardingData((prev) => ({
      ...prev,
      selectedProcedures: prev.selectedProcedures.includes(procedureId)
        ? prev.selectedProcedures.filter((id) => id !== procedureId)
        : [...prev.selectedProcedures, procedureId],
    }));
  };

  const handleInputChange = (field: string, value: string) => {
    if (field.includes('.')) {
      const [parent, child] = field.split('.');
      setOffboardingData((prev) => ({
        ...prev,
        [parent]: {
          ...(prev[parent as keyof OffboardingDataType] as ContactDetails),
          [child]: value,
        },
      }));
    } else {
      setOffboardingData((prev) => ({
        ...prev,
        [field]: value,
      }));
    }
  };

  const calculateEstimatedDuration = () => {
    return offboardingData.selectedProcedures.reduce((total, procedureId) => {
      const procedure = offboardingProcedures.find((p) => p.id === procedureId);
      return total + (procedure?.estimatedDays || 0);
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (offboardingData.selectedProcedures.length === 0) {
      alert(t('Please select at least one offboarding procedure'));
      return;
    }

    setIsProcessing(true);

    // Simulate API call
    setTimeout(() => {
      const offboardingRecord: OffboardingRecord = {
        ...offboardingData,
        employeeId: employee.id,
        employeeName: employee.name,
        startedAt: new Date().toISOString(),
        startedBy: 'current_user_id',
        estimatedDuration: calculateEstimatedDuration(),
        status: 'in_progress',
        procedures: offboardingData.selectedProcedures.map((procedureId) => {
          const procedure = offboardingProcedures.find((p) => p.id === procedureId);
          return {
            id: procedure?.id || '',
            title: procedure?.title || '',
            description: procedure?.description || '',
            estimatedDays: procedure?.estimatedDays || 0,
            icon: procedure?.icon || '',
            status: 'pending',
            assignedAt: new Date().toISOString(),
          };
        }),
      };

      onOffboardingStarted(offboardingRecord);
      setIsProcessing(false);

      // Reset form
      setOffboardingData({
        selectedProcedures: [],
        priorityLevel: 'standard',
        expectedCompletionDate: '',
        assignedTo: '',
        notes: '',
        contactDetails: {
          personalEmail: '',
          personalPhone: '',
          emergencyContact: '',
        },
      });
    }, 2000);
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-red-100 rounded-xl flex items-center justify-center">
              <UserMinus className="w-5 h-5 text-red-600" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-slate-900">{t('Start Offboarding Workflow')}</h3>
              <p className="text-slate-600">{t('Initialize employee departure procedures')}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-400" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Employee Summary */}
          <div className="bg-slate-50 rounded-xl p-4">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-slate-500 mb-1">{t('Employee')}</p>
                <p className="font-semibold text-slate-900">{employee.name}</p>
                <p className="text-sm text-slate-600">{employee.employeeId}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">{t('Department')}</p>
                <p className="font-medium text-slate-700">{employee.department}</p>
              </div>
              <div>
                <p className="text-sm text-slate-500 mb-1">{t('Status')}</p>
                <div className="flex items-center space-x-2">
                  <div className={`w-2 h-2 rounded-full ${eosStatus.isPaid ? 'bg-green-500' : 'bg-orange-500'}`}></div>
                  <span className="text-sm font-medium">{eosStatus.isPaid ? t('EOS Paid') : t('EOS Pending')}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Offboarding Procedures */}
          <div>
            <h4 className="text-lg font-semibold text-slate-900 mb-4">{t('Select Offboarding Procedures')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {offboardingProcedures.map((procedure) => (
                <div
                  key={procedure.id}
                  className={`relative p-4 rounded-xl border-2 cursor-pointer transition-all duration-200 ${
                    offboardingData.selectedProcedures.includes(procedure.id)
                      ? 'border-blue-300 bg-blue-50'
                      : 'border-border bg-card hover:border-border'
                  }`}
                  onClick={() => handleProcedureToggle(procedure.id)}
                >
                  <div className="flex items-start space-x-3">
                    <Checkbox
                      checked={offboardingData.selectedProcedures.includes(procedure.id)}
                      onChange={() => handleProcedureToggle(procedure.id)}
                    />
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <span className="text-slate-600">{iconMap[procedure.icon]}</span>
                        <h5 className="font-medium text-slate-900">{procedure.title}</h5>
                        <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded-full">
                          {procedure.estimatedDays} {procedure.estimatedDays !== 1 ? t('days') : t('day')}
                        </span>
                      </div>
                      <p className="text-sm text-slate-600">{procedure.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Estimated Duration */}
          {offboardingData.selectedProcedures.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-800">
                  {t('Estimated Total Duration')}: {calculateEstimatedDuration()} {t('working days')}
                </span>
              </div>
            </div>
          )}

          {/* Assignment Details */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">{t('Assigned To')}</label>
              <input
                type="text"
                value={offboardingData.assignedTo}
                onChange={(e) => handleInputChange('assignedTo', e.target.value)}
                placeholder={t('HR coordinator or manager')}
                className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                {t('Expected Completion Date')}
              </label>
              <input
                type="date"
                value={offboardingData.expectedCompletionDate}
                onChange={(e) => handleInputChange('expectedCompletionDate', e.target.value)}
                className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
              />
            </div>
          </div>

          {/* Contact Details */}
          <div className="space-y-4">
            <h4 className="text-lg font-semibold text-slate-900">{t('Personal Contact Details')}</h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('Personal Email')}</label>
                <input
                  type="email"
                  value={offboardingData.contactDetails.personalEmail}
                  onChange={(e) => handleInputChange('contactDetails.personalEmail', e.target.value)}
                  placeholder="personal@email.com"
                  className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('Personal Phone')}</label>
                <input
                  type="tel"
                  value={offboardingData.contactDetails.personalPhone}
                  onChange={(e) => handleInputChange('contactDetails.personalPhone', e.target.value)}
                  placeholder="+971 50 123 4567"
                  className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">{t('Emergency Contact')}</label>
                <input
                  type="tel"
                  value={offboardingData.contactDetails.emergencyContact}
                  onChange={(e) => handleInputChange('contactDetails.emergencyContact', e.target.value)}
                  placeholder={t('Emergency contact number')}
                  className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary"
                />
              </div>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-2">{t('Additional Notes')}</label>
            <textarea
              value={offboardingData.notes}
              onChange={(e) => handleInputChange('notes', e.target.value)}
              placeholder={t('Special instructions, timeline requirements, or other important information...')}
              rows={3}
              className="w-full px-3 py-2 border border-border rounded-lg focus-visible:ring-2 focus-visible:ring-primary/20 focus-visible:border-primary resize-none"
            />
          </div>

          {/* Information Notice */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Info className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-amber-800">{t('Workflow Information')}</p>
                <p className="text-sm text-amber-700 mt-1">
                  {t(
                    'The offboarding workflow will remain available and can be managed even after EOS payment is completed. Selected procedures will be assigned to the specified coordinator for tracking and completion.'
                  )}
                </p>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex space-x-4 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-border rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
              disabled={isProcessing}
            >
              {t('Cancel')}
            </button>
            <button
              type="submit"
              className="flex-1 flex items-center justify-center space-x-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50"
              disabled={isProcessing}
            >
              {isProcessing ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>{t('Starting Workflow...')}</span>
                </>
              ) : (
                <>
                  <UserMinus className="w-4 h-4" />
                  <span>{t('Start Offboarding')}</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
